import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  signal,
  viewChild,
} from '@angular/core';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { CountryService } from '../../services/country.service';
import { ITimezoneOption, toTimezoneOption } from './timezone-option';

@Component({
  selector: 'app-timezone-input',
  templateUrl: './timezone-input.component.html',
  styleUrls: ['./timezone-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatFormFieldModule, MatInputModule, MatAutocompleteModule],
})
export class TimezoneInputComponent {
  readonly timezone = model.required<string>();
  readonly label = input('Timezone');
  readonly helperText = input();
  readonly testId = input<string>();
  readonly required = input(false, { transform: booleanAttribute });

  private readonly touched = signal(false);

  private readonly countryService = inject(CountryService);

  private readonly timezoneNames = new Set(
    this.countryService.getAllTimezones()
  );

  /** Required timezones must be set to one of the available timezones */
  readonly isValid = computed(() => !this.getValidationError());

  readonly error = computed(() =>
    this.touched() ? this.getValidationError() : null
  );

  /** There's no form control, so the error state comes from `error` */
  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => !!this.error(),
  };

  private readonly matInput = viewChild.required(MatInput);

  private readonly availableTimezoneOptions = [...this.timezoneNames].map(
    toTimezoneOption
  );
  private readonly timezoneOptionsMap = new Map<string, ITimezoneOption>(
    this.availableTimezoneOptions.map((option) => [option.value, option])
  );

  readonly filteredTimezoneOptions = signal<ITimezoneOption[]>(
    this.availableTimezoneOptions
  );
  readonly timezoneSearchTerm = linkedSignal(() =>
    this.resolveTimezoneLabel(this.timezone())
  );

  constructor() {
    effect(() => {
      this.error();
      this.matInput().updateErrorState();
    });
  }

  onTimezoneSearch({ target }: Event) {
    const inputValue = (target as HTMLInputElement).value || '';
    this.timezoneSearchTerm.set(inputValue);

    const search = inputValue.trim().toLowerCase();

    if (search) {
      this.filteredTimezoneOptions.set(
        this.availableTimezoneOptions.filter((tz) =>
          tz.searchTokens.some((token) => token.includes(search))
        )
      );
    } else {
      this.resetFilteredTimezones();
    }
  }

  onTimezoneOptionSelected({
    option: { value },
  }: MatAutocompleteSelectedEvent) {
    this.timezone.set(value);
    this.timezoneSearchTerm.set(this.resolveTimezoneLabel(value));
    this.resetFilteredTimezones();
  }

  onTimezoneAutocompleteClosed() {
    this.timezoneSearchTerm.set(this.resolveTimezoneLabel(this.timezone()));
    this.resetFilteredTimezones();
  }

  onTimezoneInputFocus() {
    this.resetFilteredTimezones();
  }

  touch() {
    this.touched.set(true);
  }

  private getValidationError() {
    if (!this.required()) {
      return null;
    }

    const timezone = this.timezone();

    if (!timezone) {
      return 'Required';
    }

    return this.timezoneNames.has(timezone) ? null : 'Select a valid timezone';
  }

  private resolveTimezoneLabel(ianaValue: string): string {
    const option = this.timezoneOptionsMap.get(ianaValue);
    return option ? option.label : ianaValue;
  }

  private resetFilteredTimezones() {
    this.filteredTimezoneOptions.set(this.availableTimezoneOptions);
  }
}
